import React, {
	Dispatch,
	Fragment,
	SyntheticEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import {
	close,
	heartFilled as HeartFilled,
	heart as HeartOutlined,
	pause as pauseIcon,
	play as playIcon,
	upgrade,
	warning,
} from '@/assets/icons';
// import { Scrollbar } from '@/components/Scrollbar';
import { AsaphButton } from '@/components/shared/Button';
import { Loader } from '@/components/shared/Loader';
import { LinkFrom } from '@/config/constants';
import { dashboardUrl, videoUrl as videoUrlConst } from '@/config/urls';
import { Maybe } from '@/graphql';
// import MarkdownPreview from '@uiw/react-markdown-preview';
// import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';
import { timeFormatter } from '@/helpers/timeFormatter';
import { useIsMounted } from '@/hooks/useIsMounted';
import { useMediaDomainUrl } from '@/hooks/useMediaDomainUrl';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useSubscription } from '@/providers/SubscriptionProvider';
import { Capacitor } from '@capacitor/core';
import { CircularProgress, Slider, SvgIcon } from '@material-ui/core';
import Hls from 'hls.js';
import { isNil } from 'ramda';
import { Link, useNavigate } from 'react-router-dom';

// import { Link } from 'react-router-dom';
import { ControlButton } from '../ControlButton';
// import { formatTimeRemaining } from '@/helpers/formatTimeRemaining';
import { IconButton } from '../IconButton';
// import { TimeRemaining } from './Devotion/TimeRemaining';
// import { MiniControls } from '../MiniControls';
import { PrevNextButton } from '../PrevNextButton/';
import styles from './Player.module.scss';

type InitialVideoPlayObject = {
	videoElement: React.RefObject<HTMLVideoElement>;
	hls: Hls;
	setVideoReady: React.Dispatch<React.SetStateAction<boolean>>;
	play: () => void;
	preload?: boolean;
	playing?: boolean;
};

const initialVideoPlay = ({
	videoElement,
	hls,
	setVideoReady,
	play,
	preload,
	playing,
}: InitialVideoPlayObject) => {
	if (videoElement.current) {
		hls.startLoad(0);
		videoElement.current.currentTime = 0;
		// hls.currentLevel = Math.floor((hls.levels.length - 1) / 2);
		hls.currentLevel = -1;
		setVideoReady(true);
		if (preload || playing) {
			play();
		}
	}
};

interface IProps {
	videoId: string;
	url: string | null;
	name: string;
	/** Start the video at x seconds */
	posterUrl: string;
	onPercentagePlayedChange?(value: number): void;
	onVideoEnd?(): void;
	className?: string;
	playing?: boolean;
	preload?: boolean;
	muted?: boolean;
	setMuted: (state: boolean) => void;
	description?: string | null;
	contributor: {
		name?: string | null;
		imageUrl?: string | null;
	};
	videoLink: string;
	videoName?: string;
	liked: boolean;
	onLikeClick: (state: boolean) => void;
}

export const Player: React.FC<IProps> = ({
	videoId,
	url,
	name,
	posterUrl,
	onVideoEnd,
	className,
	playing,
	preload,
	muted = true,
	setMuted,
	description,
	contributor,
	videoLink,
	videoName,
	liked,
	onLikeClick,
}) => {
	const isCapacitor = Capacitor.isNativePlatform();
	const isMounted = useIsMounted();
	const videoUrl = useMediaDomainUrl(url || '') || null;
	const navigate = useNavigate();

	const [hls] = useState<Hls>(
		new Hls({
			xhrSetup: (xhr) => {
				xhr.withCredentials = false; // do send cookies
				xhr.setRequestHeader(
					'Access-Control-Allow-Headers',
					'X-Requested-With, Content-Type, Accept',
				);
				xhr.setRequestHeader(
					'Access-Control-Allow-Origin',
					window.location.origin,
				);
				xhr.setRequestHeader(
					'Access-Control-Allow-Credentials',
					'true',
				);
			},
			startLevel: -1,
		}),
	);
	const videoElement = useRef<HTMLVideoElement>(null);
	const [videoReady, setVideoReady] = useState<boolean>(false);
	const [videoEnded, setVideoEnded] = useState<boolean>(false);
	const [playerSupported, setPlayerSupported] = useState<boolean>(true);
	const [controlsActive, setControlsActive] = useState<boolean>(false);
	const [isPaused, setIsPaused] = useState<boolean>(true);
	const [playError, setPlayError] = useState<boolean>(false);
	// remains for a possible later use
	const [currentTime, setCurrentTime] = useState<string>('0:00');
	const [remainingTime, setRemainingTime] = useState<string>('0:00');
	const [playPercentage, setPlayPercentage] = useState<number>(0);
	const [videoMilestone, setVideoMilestone] = useState<
		0 | 1 | 25 | 50 | 75 | 100
	>(0);

	const fireReelProgressEvent = useAnalyticsEvent('Reels_Reel_Progress');
	// const fireNavigateVideoEvent = useAnalyticsEvent(
	// 	'Reels_Reel_Navigate_Video',
	// );

	const applyVideoAction = useCallback(
		(action: (video: HTMLVideoElement) => void): boolean => {
			if (videoElement.current) {
				action(videoElement.current);
				return true;
			}
			return false;
		},
		[],
	);

	const pause = useCallback(() => {
		applyVideoAction((video) => {
			video.pause();
		});
	}, [applyVideoAction]);

	const play = useCallback(() => {
		applyVideoAction((video) => {
			setPlayError(false);
			const playPromise = video.play();
			// In browsers that don’t yet support this functionality,
			// playPromise won’t be defined.
			if (playPromise !== undefined) {
				playPromise.catch(function (error) {
					// Automatic playback failed.
					// Show a UI element to let the user manually start playback.
					setPlayError(true);
				});
			}
		});
	}, [applyVideoAction]);

	useEffect(() => {
		if (videoElement.current) {
			videoElement.current.currentTime = 0;
		}
		if (playing) {
			play();
		} else {
			pause();
		}
	}, [playing, pause, play]);

	useEffect(() => {
		if (preload) {
			play();
		} else {
			pause();
		}
	}, [preload, pause, play]);

	useEffect(() => {
		return () => {
			hls.destroy();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (
			videoElement.current &&
			!videoElement.current.canPlayType(
				'application/vnd.apple.mpegurl',
			) &&
			!Hls.isSupported()
		) {
			setPlayerSupported(false);
		}
	}, [videoElement]);

	useEffect(() => {
		if (videoElement.current && isMounted.current) {
			if (videoUrl && Hls.isSupported()) {
				hls.loadSource(videoUrl);
				hls.attachMedia(videoElement.current);
				hls.on(Hls.Events.MANIFEST_PARSED, () =>
					initialVideoPlay({
						hls,
						play,
						setVideoReady,
						videoElement,
						playing,
						preload,
					}),
				);
			} else if (
				videoElement.current.canPlayType(
					'application/vnd.apple.mpegurl',
				) &&
				videoUrl
			) {
				// Case for Safari
				videoElement.current.src = videoUrl;
				videoElement.current.addEventListener('loadedmetadata', () =>
					initialVideoPlay({
						hls,
						play,
						setVideoReady,
						videoElement,
						playing,
						preload,
					}),
				);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [videoUrl]);

	useEffect(() => {
		if (videoElement.current) {
			const video = videoElement.current;

			// Attach the events listener to the video element
			video.addEventListener('durationchange', () => {
				setRemainingTime(
					timeFormatter(video.duration - video.currentTime),
				);
				const value = video.currentTime / video.duration;
				setPlayPercentage(value);
			});
			video.addEventListener('play', () => {
				setVideoEnded(false);
				setIsPaused(false);
			});
			video.addEventListener('pause', () => {
				setIsPaused(true);
			});
			// remains for a possible later use
			// video.addEventListener('volumechange', (e) => {
			// 	if (isMounted.current) {
			// 		setIsMuted(!!video.muted);
			// 	}
			// });
			video.addEventListener('timeupdate', () => {
				setVideoEnded(false);
				// remains for a possible later use
				setCurrentTime(timeFormatter(video.currentTime));
				setRemainingTime(
					timeFormatter(video.duration - video.currentTime),
				);
				setPlayPercentage(video.currentTime / video.duration);
			});
			video.addEventListener('ended', () => {
				if (!isNil(onVideoEnd)) {
					onVideoEnd();
				}

				setVideoEnded(true);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [videoElement, play]);

	useEffect(() => {
		switch (true) {
			case playPercentage > 0.9:
				setVideoMilestone(100);
				break;
			case playPercentage > 0.74:
				setVideoMilestone(75);
				break;
			case playPercentage > 0.49:
				setVideoMilestone(50);
				break;
			case playPercentage > 0.24:
				setVideoMilestone(25);
				break;
			case playPercentage > 0:
				setVideoMilestone(1);
				break;
		}
	}, [playPercentage]);

	useEffect(() => {
		if (videoMilestone > 0 && playing) {
			fireReelProgressEvent({
				name: name,
				progress: videoMilestone,
				loopCount: 0,
			});
		}
	}, [fireReelProgressEvent, videoMilestone, name, playing]);

	const isVideoLoading =
		!playing || playPercentage === 0 || isNaN(playPercentage);

	const avatar = contributor.imageUrl || '/assets/icon/asaph-logo-icon.png';
	const contributorName = contributor.name || '';

	return (
		<div className={[styles['player'], className].join(' ')} key={videoId}>
			{!playerSupported ? (
				<div className="player-error">
					<SvgIcon
						component={warning}
						className="player-error__icon"
					/>
					<div className="player-error__text">
						Unfortunately your browser doesn't support HLS files.
					</div>
				</div>
			) : (
				<div className={styles['video-dimensions']}>
					<video
						ref={videoElement}
						playsInline={true}
						autoPlay={!playError && (preload || playing)}
						muted={muted || !playing}
						loop
					/>

					{isVideoLoading && (
						<div
							style={{ backgroundImage: `url(${posterUrl})` }}
							className={styles['poster']}
						>
							<CircularProgress />
						</div>
					)}
					<div className={styles['overlay']}>
						{/* <div className="player-scrubber__play">
							<ControlButton
								icon={isPaused ? playIcon : pauseIcon}
								ariaLabel={isPaused ? 'play' : 'paused'}
								size="xsmall"
								onClick={togglePlayPause}
							/>
						</div> */}
						<div className={styles['gradient']} />

						<div className={styles['bottomRow']}>
							<div className={styles['left']}>
								{/* <div
									className={styles['button']}
									onClick={() => {
										fireNavigateVideoEvent({
											name,
											progress:
												Math.round(
													playPercentage * 100,
												) / 100,
											videoName: videoName || videoLink,
										});
										navigate(videoLink);
									}}
								>
									Watch full episode
								</div> */}
								<div className={styles['contributor']}>
									<img
										src={avatar}
										className={styles['avatar']}
									/>
									<div className={styles['details']}>
										<div className={styles['name']}>
											{contributorName}
										</div>
										<div className={styles['description']}>
											{description}
										</div>
									</div>
								</div>
							</div>
							<div
								className={styles['right']}
								onClick={() => onLikeClick(!liked)}
							>
								{/* <SvgIcon component={heartFilled} /> */}
								{liked ? <HeartFilled /> : <HeartOutlined />}
								{liked}
							</div>
						</div>

						<div className={styles['timeline']}>
							<div
								className={styles['bar']}
								style={{ width: `${playPercentage * 100}%` }}
							/>
						</div>

						{/* <div className={styles['debug']}>
							<div>{playing ? 'Playing' : 'Paused'}</div>
							<div>{preload && 'Preloading...'}</div>
						</div> */}
						{/* <div className="player-scrubber__time player-scrubber__time--remaining">
								{remainingTime}
							</div> */}
					</div>
				</div>
			)}
		</div>
	);

	function togglePlayPause() {
		if (isPaused) {
			play();
			setControlsActive(false);
		} else {
			pause();
		}
	}

	// remains for a possible later use
	// function skipToStart() {
	// 	applyVideoAction((video) => {
	// 		video.currentTime = 0;
	// 		video.play();
	// 	});
	// }

	// remains for a possible later use
	// function toggleVolume() {
	// 	if (isMuted) {
	// 		unmute();
	// 	} else {
	// 		mute();
	// 	}
	// }

	// function mute() {
	// 	applyVideoAction((video) => {
	// 		video.muted = true;
	// 	});
	// }

	// function unmute() {
	// 	applyVideoAction((video) => {
	// 		video.muted = false;
	// 	});
	// }

	function seek(
		_event: React.ChangeEvent<{}> | null,
		value: number | number[],
	) {
		// Convert the value into time
		if (typeof value === 'number') {
			// setPlayPercentage(value);
			applyVideoAction((video) => {
				video.currentTime = value * video.duration;
			});
		}
	}

	function handlePlayerControlsClick(event: SyntheticEvent) {
		// This helps to handle situation when you click on any icon on the controls panel
		// but you don't want to hide it yet
		const isCurrentElementSourceOfEvent =
			event.target === event.currentTarget;
		if (isCurrentElementSourceOfEvent) {
			setControlsActive(false);
		}
	}
};
